import { useCallback, useState, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import {
  faChevronLeft,
  faChevronRight,
  faSave
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@morressier/alembic';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ISubmissionAuthor,
  ISubmissionFormData,
  useFormContext
} from 'src/contexts/FormContext';
import { ajvResolver } from '@hookform/resolvers/ajv';

import { generateValidationSchema } from 'src/utils/validation';
import AuthorList from './components/AuthorList';
import SubmissionManagement from './components/SubmissionManagement';
import CoAuthorConsent from './components/CoAuthorConsent';
import AuthorListEditModal from './components/AuthorListEditModal';
import StepHeader from './components/StepHeader';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import DeleteDraft from './DeleteDraft';
import { Banner } from '@morressier/alembic';
import { pluralizeString } from 'src/utils/string';
import { useDuplicateEmails } from 'src/hooks/useDuplicateEmails';
import { fetchUserProfileData } from 'src/api/signUpMutation';
import { useQuery } from '@tanstack/react-query';
import { omit } from 'lodash';
import { IAuthorStepConfig } from 'src/hooks/useSubmissionStepsConfig';
import { SidebarLayout } from 'src/layouts/SidebarLayout';
import { SubmissionSidebar } from './SubmissionSidebar';
import { IUser } from '@publishing-engine/domain/models';
import { useSubmissionLeaveGuardRegistration } from './SubmissionLeaveGuard';

interface Props {
  currentStep: number;
  totalSteps: number;
  draftSubmissionId: string;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  autosaveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId: string,
    draftStep?: number
  ) => Promise<unknown>;
  saveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId?: string
  ) => void;
  stepConfigs: unknown;
  goToStep: (step: number) => void;
}

const AuthorStep: React.FC<Props> = ({
  goToNextStep,
  goToPreviousStep,
  saveDraftSubmission,
  currentStep,
  totalSteps,
  autosaveDraftSubmission,
  draftSubmissionId,
  stepConfigs,
  goToStep
}) => {
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const { formData, updateFormData } = useFormContext();
  const { submissionContext } = useSubmissionContextData();
  const reviewAuthorInformation =
    submissionContext?.config.cms.authorSubmissionForm.reviewAuthorInformation;
  const submissionContextId = formData.submissionContext.submissionContextId;
  const configs = stepConfigs as IAuthorStepConfig;
  const hasSubmissionManagement =
    !!configs.ContactAuthor ||
    !!configs.CorrespondingAuthor ||
    !!configs.SubmittingAuthor;

  const schema = generateValidationSchema(configs);

  const methods = useForm<ISubmissionFormData>({
    defaultValues: formData,
    resolver: ajvResolver(schema),
    mode: 'onChange'
  });
  const { handleSubmit, getValues, trigger } = methods;
  const isDirty = methods.formState.isDirty;

  const {
    formState: { errors }
  } = methods;

  const authors = methods.getValues('metadata.authors') || [];
  const authorAffiliationData = methods.watch('metadata.authors') || [];
  const { duplicateCount, authorsWithDuplicateEmailsCount } =
    useDuplicateEmails<ISubmissionAuthor>(authors);
  const isEmpty = (value?: string) => value == null || value.trim() === '';
  const hasNotCountryAndCity = authorAffiliationData.some(
    author => isEmpty(author.country) || isEmpty(author.city)
  );
  const hasAuthorErrors =
    !!errors.submittingAuthor ||
    !!errors.correspondingAuthor ||
    !!errors.contactAuthor ||
    !!errors.metadata?.authors ||
    duplicateCount > 0 ||
    hasNotCountryAndCity;

  const authorBannerErrorMessage =
    `Some author entries have missing data (names, emails, affiliations, ORCID)` +
    (duplicateCount > 0
      ? ` and we have detected ${authorsWithDuplicateEmailsCount} ${pluralizeString(
          'author',
          authorsWithDuplicateEmailsCount
        )} with duplicated ${pluralizeString('email', duplicateCount)}`
      : '') +
    `. Although your edits were saved, you must fix these issues now. Check for duplicates and fill all required fields before proceeding.`;

  const onSubmit = async () => {
    // Prevent multiple clicks
    if (isNavigating) return;

    setIsNavigating(true);

    try {
      const values = getValues();
      updateFormData(values);

      if (duplicateCount > 0 || hasAuthorErrors || hasNotCountryAndCity) {
        if (bannerRef.current) {
          bannerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
        return;
      }

      await autosaveDraftSubmission(values, draftSubmissionId);
      goToNextStep();
    } finally {
      setIsNavigating(false);
    }
  };

  const onBack = () => {
    updateFormData(getValues());
    goToPreviousStep();
  };

  const onSaveDraft = () => {
    saveDraftSubmission(getValues(), draftSubmissionId);
  };
  const saveCurrentProgress = useCallback(async () => {
    const values = getValues();
    updateFormData(values);
    const results: any = await autosaveDraftSubmission(
      values,
      draftSubmissionId,
      currentStep + 2
    );

    if (results?.response?.isSuccess && results?.response?.draft) {
      updateFormData(results.response.draft);
    }

    return results?.response?.isSuccess;
  }, [autosaveDraftSubmission, currentStep, draftSubmissionId, getValues, updateFormData]);

  const onNavigateSave = () => {
    void saveCurrentProgress();
  };
  const { data: userData } = useQuery({
    queryKey: ['user-profile-data'],
    queryFn: () => fetchUserProfileData()
  });

  const authorData = userData?.data;
  const currentUserData = {
    email: authorData?.user.email ?? '',
    firstName: authorData?.user?.firstName ?? '',
    lastName: authorData?.user?.lastName ?? '',
    country: authorData?.affiliationDetails?.country ?? '',
    countryCode: authorData?.affiliationDetails?.countryCode ?? '',
    city: authorData?.affiliationDetails?.city ?? '',
    id: authorData?.user?.id ?? '',
    affiliationDetails: authorData?.affiliationDetails,
    orcid: authorData?.user?.orcid || ''
  };
  const currentUser = {
    ...currentUserData,
    ...omit(authorData?.user, ['status', 'countryCode', 'country'])
  };
  useSubmissionLeaveGuardRegistration({
    hasUnsavedChanges: isDirty,
    saveCurrentProgress
  });
  if (!reviewAuthorInformation) return <h1>Missing Information</h1>;
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, scrollToFirstError)}>
        <SidebarLayout
          sidebar={
            <SubmissionSidebar
              currentStep={currentStep}
              onNavigate={step => {
                goToStep(step);
                onNavigateSave();
              }}
            />
          }
        >
          <StepHeader
            currentStep={currentStep}
            totalSteps={totalSteps}
            heading={reviewAuthorInformation.header}
            description={reviewAuthorInformation.subHeader}
          />
          {openEditModal ? (
            <AuthorListEditModal
              config={configs}
              onClose={() => {
                // re-run the validation for the authors data, so we can show or hide the error banner
                trigger('metadata.authors');
                setOpenEditModal(false);
              }}
            />
          ) : (
            <Flex
              flexDirection="column"
              gap={{ base: 6, md: 8 }}
              alignItems={{ base: 'stretch', md: 'flex-start' }}
            >
              {hasAuthorErrors && (
                <div
                  ref={bannerRef}
                  data-cy="validation-errors-banner"
                  style={{ width: '100%', marginBottom: 16 }}
                >
                  <Banner
                    variant="error"
                    heading="Missing Author Information"
                    content={authorBannerErrorMessage}
                  />
                </div>
              )}
              {/* ----- Authors ----- */}

              {configs.Authors && (
                <AuthorList
                  showAuthorEditModal={() => setOpenEditModal(true)}
                  config={configs.Authors}
                />
              )}
              {/* ----- Submission Management ----- */}

              {hasSubmissionManagement && (
                <SubmissionManagement
                  configs={configs}
                  user={currentUser as IUser}
                />
              )}
              <CoAuthorConsent configs={configs} />
            </Flex>
          )}
        </SidebarLayout>

        <Flex
          padding={{ base: 3, md: 4 }}
          bottom={0}
          zIndex={1}
          justifyContent="center"
          position="sticky"
          bgColor="surfacePrimaryFill"
          marginX="auto"
        >
          <Flex
            width="100%"
            justifyContent="space-between"
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 2, md: 0 }}
          >
            {/* ----- Footer Buttons ----- */}
            <Button
              variant="secondary"
              onClick={async () => {
                const isValid = await trigger();
                if (isValid) {
                  onBack();
                  onNavigateSave();
                }
              }}
              width={{ base: '100%', md: 'auto' }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              Back
            </Button>
            <Flex
              gap={{ base: 2, md: 4 }}
              direction={{ base: 'column', md: 'row' }}
              width={{ base: '100%', md: 'auto' }}
            >
              {draftSubmissionId && (
                <DeleteDraft
                  submissionId={draftSubmissionId}
                  submissionContextId={submissionContextId}
                  isDisabled={isNavigating}
                />
              )}
              <Button
                variant="secondary"
                leftIcon={<FontAwesomeIcon icon={faSave} />}
                onClick={() => onSaveDraft()}
                disabled={isNavigating}
                width={{ base: '100%', md: 'auto' }}
              >
                Save Draft
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isNavigating}
                width={{ base: '100%', md: 'auto' }}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default AuthorStep;
